// ChapterContentPanel.tsx
// NOTE: Update frontend/src/__tests__/ChapterContentPanel.test.ts when editing helper logic.

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Button,
  Tooltip,
  Tabs,
  Dropdown,
  Menu,
  message,
  Modal
} from 'antd';
import {
  FileTextOutlined,
  TranslationOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  ExportOutlined,
  SaveOutlined,
  CloseOutlined,
  MoreOutlined
} from '@ant-design/icons';

import ActiveLanguageSelector, { ActiveLanguageOption } from './components/ActiveLanguageSelector';
import ChapterTextEditor from './ChapterTextEditor';
import { countWordsIncludingMarkup } from './utils/wordcount';

import { Chapter, Book } from './types';
import { removeHyperlinks } from './ProofreadTabContent';
import { useTheme } from './theme/ThemeProvider';
import type { AskAIInvocationPayload } from './AskAIModal';
import { getSavedProofreadTextForRow, hasPendingProofreadReviewForRow } from './utils/proofreadState';
import { splitLanguagePair } from './utils/languagePairs';

const { TabPane } = Tabs;

type ChapterVariantStatus = 'ready' | 'missing' | 'processing' | 'attention';


interface ChapterContentPanelProps {
  selectedChapter: Chapter;
  book: Book;
  selectedTab: string;
  handleTabChange: (value: string) => void;
  formatLangDisplayText: (pair: string) => string;
  rejectedChapters: string[];
  handleEditToggle: () => void;
  toggleTranslateModal: () => void;
  toggleProofreadModal: (nextVersion?: string) => void;
  handleExportBookClick: () => void;
  renderProofreadTabContent: (
    sourceLang: string,
    targetLang: string,
    tabActionsContainer?: HTMLElement | null,
    isActive?: boolean
  ) => {
    content: React.ReactNode;
    isProofread: boolean;
    editingNotesContent?: React.ReactNode;
    hasEditingNotes?: boolean;
  };
  renderTranslatedTabContent: (
    sourceLang: string,
    targetLang: string,
    tabActionsContainer?: HTMLElement | null,
    isActive?: boolean
  ) => { content: React.ReactNode; isTranslated: boolean };
  handleDeleteSingleTranslatedChapter: (
    sourceLang: string,
    targetLang: string
  ) => void;
  isEditing: boolean;
  handleTranslateBookClick: () => void;
  handleProofreadBookClick: () => void;
  onSaveChapterContent?: (
    chapterId: string,
    content: string,
    contentType: string,
    wordCount?: number
  ) => Promise<void>;
  onRevertChapterContent?: (
    chapterId: string,
    contentType: string
  ) => Promise<void>;
  editLocked?: boolean;
  proofreadProcessing?: boolean;
  editLockReason?: string;
  // Admin Ask AI integration
  isAdmin?: boolean;
  onAskAI?: (args: AskAIInvocationPayload) => void;
  bookId: string;
  userId: string;
  apiBase: string;
  bookLanguage: string;
  targetLanguage: string;
  selectedAIModel?: string;
  onChapterRelationshipsChange?: (
    chapterGuid: string,
    updates: Partial<Chapter>
  ) => void;
  onRunFrenchDialogueLegacyCorrection?: (params: {
    pair: string;
    targetLanguage: string;
    chapterGuid?: string;
    chapterName?: string;
  }) => void;
}

export const hasProofreadContent = (
  selectedTab: string,
  selectedChapter: Chapter,
  book: Book
): boolean => {
  if (selectedTab === 'Original') {
    return Boolean(selectedChapter.proofread_chapter && selectedChapter.proofread_chapter.length);
  }
  const { sourceLang, targetLang } = splitLanguagePair(selectedTab);
  if (!sourceLang || !targetLang) {
    return false;
  }
  const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
  const translatedChapters: any[] = (book as any)[translatedChaptersKey] || [];
  const translatedChapter = translatedChapters.find(
    (tc: any) => tc.chapter_guid === selectedChapter.chapter_guid
  );
  return Boolean(getSavedProofreadTextForRow(translatedChapter));
};

export const getContentForTab = (
  selectedTab: string,
  activeEditTab: '1' | '2' | '3',
  selectedChapter: Chapter,
  book: Book
): string => {
  if (activeEditTab === '3') {
    return '';
  }
  if (selectedTab === 'Original') {
    return activeEditTab === '1'
      ? selectedChapter.content || ''
      : selectedChapter.proofread_chapter || '';
  }
  const { sourceLang, targetLang } = splitLanguagePair(selectedTab);
  if (!sourceLang || !targetLang) {
    return '';
  }
  const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
  const translatedChapters: any[] = (book as any)[translatedChaptersKey] || [];
  const translatedChapter = translatedChapters.find(
    (tc: any) => tc.chapter_guid === selectedChapter.chapter_guid
  );
  if (activeEditTab === '1') {
    return translatedChapter?.translated_text || '';
  }
  return getSavedProofreadTextForRow(translatedChapter);
};

const ChapterContentPanel: React.FC<ChapterContentPanelProps> = ({
  selectedChapter,
  book,
  selectedTab,
  handleTabChange,
  formatLangDisplayText,
  rejectedChapters,
  handleEditToggle,
  toggleTranslateModal,
  toggleProofreadModal,
  handleExportBookClick,
  renderProofreadTabContent,
  renderTranslatedTabContent,
  handleDeleteSingleTranslatedChapter,
  isEditing,
  handleTranslateBookClick,
  handleProofreadBookClick,
  onSaveChapterContent,
  onRevertChapterContent,
  editLocked = false,
  proofreadProcessing = false,
  editLockReason,
  bookLanguage,
  isAdmin,
  onAskAI,
  onRunFrenchDialogueLegacyCorrection,
}) => {
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [activeEditTab, setActiveEditTab] = useState<'1' | '2' | '3'>('1');
  const [revertPendingKey, setRevertPendingKey] = useState<string | null>(null);
  const [localReverted, setLocalReverted] = useState(false);
  const [contentModified, setContentModified] = useState(false);
  const [editBaseline, setEditBaseline] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [tabActionsContainer, setTabActionsContainer] = useState<HTMLDivElement | null>(null);
  const [preservedStyles, setPreservedStyles] = useState<{ rawTags: string[]; cssText: string }>({
    rawTags: [],
    cssText: ''
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const { isDark } = useTheme();
  const originalLanguageLabel = (bookLanguage || 'Original').trim() || 'Original';
  const editLockMessage =
    editLockReason ||
    'This chapter is currently being translated or proofread. Please wait for it to finish before editing.';

  useEffect(() => {
    // Reset scroll position to top when switching to chapter content or changing chapters
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
    
    // Reset window scroll as well
    window.scrollTo(0, 0);
  }, [selectedChapter.chapter_guid]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [selectedTab]);


  const checkIfProofreadExists = (): boolean =>
    hasProofreadContent(selectedTab, selectedChapter, book);

  const getContentForActiveTab = () =>
    getContentForTab(selectedTab, activeEditTab, selectedChapter, book);

  // Helper: get translated chapter object for current selectedTab
  const getTranslatedChapterForSelectedTab = () => {
    if (selectedTab === 'Original') return null;
    const { sourceLang, targetLang } = splitLanguagePair(selectedTab);
    if (!sourceLang || !targetLang) return null;
    const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
    const translatedChapters: any[] = (book as any)[translatedChaptersKey] || [];
    return translatedChapters.find(
      (tc: any) => tc.chapter_guid === selectedChapter.chapter_guid
    );
  };

  // Helper: determine if backup exists for current variant
  const hasBackupForCurrentVariant = (): boolean => {
    if (activeEditTab === '3') return false;
    if (selectedTab === 'Original') {
      if (activeEditTab === '1') {
        return !!(selectedChapter as any)?.original_content_backup;
      } else {
        return !!(selectedChapter as any)?.original_proofread_backup;
      }
    } else {
      const translatedChapter = getTranslatedChapterForSelectedTab();
      if (!translatedChapter) return false;
      if (activeEditTab === '1') {
        return !!translatedChapter?.original_translated_text_backup;
      } else {
        return !!translatedChapter?.original_proofread_translated_backup;
      }
    }
  };

  // Helper: get backup for current variant (from props only; no network/db calls)
  const getBackupForCurrentVariant = (): string | undefined => {
    if (activeEditTab === '3') return undefined;
    if (selectedTab === 'Original') {
      return activeEditTab === '1'
        ? (selectedChapter as any)?.original_content_backup
        : (selectedChapter as any)?.original_proofread_backup;
    } else {
      const translatedChapter = getTranslatedChapterForSelectedTab();
      if (!translatedChapter) return undefined;
      return activeEditTab === '1'
        ? translatedChapter?.original_translated_text_backup
        : translatedChapter?.original_proofread_translated_backup;
    }
  };

  // Helper: compute current contentType (same format used by save)
  const getCurrentContentType = (): string => {
    if (activeEditTab === '3') {
      return 'editing_notes';
    }
    if (selectedTab === 'Original') {
      return activeEditTab === '1' ? 'original' : 'original_proofread';
    } else {
      const { sourceLang, targetLang } = splitLanguagePair(selectedTab);
      return activeEditTab === '1'
        ? `translated_${sourceLang}-${targetLang}`
        : `proofread_${sourceLang}-${targetLang}`;
    }
  };

  const getCurrentVariantLabel = (): string => {
    if (activeEditTab === '3') {
      return 'Editing Notes';
    }
    if (selectedTab === 'Original') {
      return activeEditTab === '1' ? originalLanguageLabel : `${originalLanguageLabel} (Proofread)`;
    }
    const base = formatLangDisplayText(selectedTab);
    return activeEditTab === '1' ? base : `${base} - Proofread`;
  };

  const getOriginalChapterHtmlForAskAI = (): string => {
    const proofreadOriginal = selectedChapter.proofread_chapter;
    if (typeof proofreadOriginal === 'string' && proofreadOriginal.trim().length > 0) {
      return proofreadOriginal;
    }
    return selectedChapter.content || '';
  };

  const getCurrentVariantKey = (): string =>
    `${selectedChapter.chapter_guid}:${getCurrentContentType()}`;

  const getRevertDescriptor = (): string => {
    if (activeEditTab === '3') {
      return 'editing notes';
    }
    if (selectedTab === 'Original') {
      return activeEditTab === '1' ? 'original chapter' : 'original proofread';
    }
    const langLabel = formatLangDisplayText(selectedTab);
    return activeEditTab === '1' ? `${langLabel} translation` : `${langLabel} proofread`;
  };

  const handleRevertContent = () => {
    if (editLocked) {
      message.warning(editLockMessage);
      return;
    }
    if (!onRevertChapterContent) return;
    const descriptor = getRevertDescriptor();
    const backupLabel =
      selectedTab === 'Original'
        ? activeEditTab === '1'
          ? 'originally uploaded text'
          : 'original AI proofread version'
        : activeEditTab === '1'
          ? 'original AI translation'
          : 'original AI proofread translation';
    Modal.confirm({
      title: `Revert ${descriptor}?`,
      icon: (
        <ExclamationCircleOutlined
          style={{ color: 'var(--text-warning, #d97706)' }}
        />
      ),
      content: `This will replace the current ${descriptor} text with the ${backupLabel} for this chapter. All changes made since then will be lost.`,
      okText: 'Revert',
      cancelText: 'Cancel',
      className: 'theme-aware-confirm',
      okButtonProps: { danger: true },
      centered: true,
      onOk: async () => {
        try {
          const contentType = getCurrentContentType();
          setRevertPendingKey(getCurrentVariantKey());
          await onRevertChapterContent(selectedChapter.chapter_guid, contentType);
          try { localStorage.setItem(`reverted:${getCurrentVariantKey()}`, '1'); } catch {}
          setLocalReverted(true);
          setRevertPendingKey(null);
          // After revert, exit editing mode if we were editing
          setIsEditingContent(false);
          setEditedContent('');
          setActiveEditTab('1');
        } catch (err) {
          console.error('Revert error:', err);
          message.error('Failed to revert chapter content');
        }
      }
    });
  };


  // Clear edit state when switching chapters or main tab
  useEffect(() => {
    setIsEditingContent(false);
    setEditedContent('');
    setActiveEditTab('1');
    setRevertPendingKey(null);
    setContentModified(false); // Reset content modified flag when switching chapters/tabs
    setHasUnsavedChanges(false);
    setEditBaseline('');
  }, [selectedChapter.chapter_guid, selectedTab]);

  /**
   * extractBodyAndStyles:
   *    Pulls any <style> blocks out of the chapter body so the editor receives
   *    only the editable markup. Returns both the stripped body HTML and the
   *    removed style tags (so we can re-attach them on save).
   */
  const extractBodyAndStyles = (
    htmlContent: any
  ): { body: string; styleTags: string[]; cssText: string } => {
    if (typeof htmlContent !== 'string') {
      return { body: '', styleTags: [], cssText: '' };
    }
    if (!htmlContent) {
      return { body: '', styleTags: [], cssText: '' };
    }
    const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    const inner = bodyMatch && bodyMatch[1] ? bodyMatch[1] : htmlContent;
    const styleRegex = /<style[^>]*>[\s\S]*?<\/style>/gi;
    const styleTags: string[] = [];
    const cssParts: string[] = [];
    const bodyWithoutStyles = inner.replace(styleRegex, (match) => {
      styleTags.push(match);
      const cssMatch = match.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
      if (cssMatch && cssMatch[1]) {
        cssParts.push(cssMatch[1].trim());
      }
      return '';
    });
    return {
      body: bodyWithoutStyles.trim(),
      styleTags,
      cssText: cssParts.join('\n')
    };
  };

  const extractBodyContent = (htmlContent: any): string => {
    return extractBodyAndStyles(htmlContent).body;
  };

  // Normalization helpers for robust backup comparison across reloads/navigations
  const normalizeHtmlForCompare = (html: string): string => {
    if (!html) return '';
    let s = html.replace(/\r\n/g, '\n'); // unify line endings
    s = s.replace(/>\s+</g, '><'); // remove whitespace between tags
    s = s.replace(/\s{2,}/g, ' '); // collapse repeated spaces
    return s.trim();
  };

  const normalizeForCompare = (html?: string): string => {
    const inner = extractBodyContent(html || '');
    return normalizeHtmlForCompare(inner);
  };

  /**
   * wrapBodyContent:
   *    Replace only the inner HTML between <body ...> and </body>,
   *    preserving the original <body> attributes and namespaces.
   */
  const wrapBodyContent = (
    editedBodyHTML: string,
    originalFullHTML: string
  ): string => {
    if (!originalFullHTML) return editedBodyHTML;
    const bodyTagRegex = /(<body\b[^>]*>)([\s\S]*?)(<\/body>)/i;
    if (bodyTagRegex.test(originalFullHTML)) {
      return originalFullHTML.replace(bodyTagRegex, `$1${editedBodyHTML}$3`);
    }
    // Fallback if no <body> wrapper: save edited content directly
    return editedBodyHTML;
  };

  const handleEditContent = () => {
    if (editLocked) {
      message.warning(editLockMessage);
      return;
    }
    const fullContent = getContentForActiveTab();
    const { body, styleTags, cssText } = extractBodyAndStyles(fullContent);
    setPreservedStyles({ rawTags: styleTags, cssText });
    setEditedContent(body);
    setEditBaseline(body);
    setHasUnsavedChanges(false);
    setIsEditingContent(true);
  };

  const handleSaveContent = async () => {
    try {
      if (editLocked) {
        message.warning(editLockMessage);
        return;
      }
      // Disallow creating a new proofread via editor if it doesn't exist yet
      if (activeEditTab === '2' && !checkIfProofreadExists()) {
        message.warning('Cannot edit proofread content before it exists. Please generate or import a proofread version first.');
        return;
      }
      if (onSaveChapterContent) {
        const originalFullContent = getContentForActiveTab();
        // Use the edited inner <body> HTML provided by the editor component
        const editedBodyContent = editedContent;
        const combinedBodyContent = `${preservedStyles.rawTags.join('')}${editedBodyContent}`;
        // Re-wrap it into the original XML structure without losing any wrapper or attributes
        const fullContentToSave = wrapBodyContent(
          combinedBodyContent,
          originalFullContent
        );

        const contentType = getCurrentContentType();

        const computedWordCount = countWordsIncludingMarkup(fullContentToSave);
        await onSaveChapterContent(
          selectedChapter.chapter_guid,
          fullContentToSave,
          contentType,
          computedWordCount
        );
        
        // Mark content as modified so the revert state logic can check if it should be cleared
        setContentModified(true);
      }
      setIsEditingContent(false);
      setEditedContent('');
      setHasUnsavedChanges(false);
      setPreservedStyles({ rawTags: [], cssText: '' });
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingContent(false);
    setEditedContent('');
    setHasUnsavedChanges(false);
    setPreservedStyles({ rawTags: [], cssText: '' });
  };

  const renderEditableContent = (content?: string) => {
    const safe = content || '';
    if (isEditingContent) {
      return (
        <div
          data-tour-id="editor-body"
          data-testid="tutorial:chapter:editor"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'visible',
            paddingBottom: 24,
            boxSizing: 'border-box'
          }}
          >
            <ChapterTextEditor
              initialContent={safe}
              styleContent={preservedStyles.cssText}
              onChange={(html) => {
                setEditedContent(html);
                try {
                setHasUnsavedChanges(
                  normalizeHtmlForCompare(html) !== normalizeHtmlForCompare(editBaseline)
                );
              } catch {
                setHasUnsavedChanges(true);
              }
            }}
          />
        </div>
      );
    }
    const contentClassName = isDark
      ? 'chapter-content-display chapter-content-display--dark'
      : 'chapter-content-display';
    return (
      <div className="chapter-content-card chapter-content-card--source">
        <div
          data-tour-id="chapter-content"
          data-testid="tutorial:book-detail:chapter-content"
          className={contentClassName}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'visible',
            paddingBottom: 24,
            boxSizing: 'border-box',
            color: 'var(--text-main)',
            background: 'transparent'
          }}
          dangerouslySetInnerHTML={{ __html: removeHyperlinks(safe) }}
        />
      </div>
    );
  };

  const hasProofread = checkIfProofreadExists();
  const hasBackup = hasBackupForCurrentVariant();
  const backupContent = getBackupForCurrentVariant();
  const currentContentForCompare = getContentForActiveTab();
  const saveDisabled = editLocked || activeEditTab === '3' || (activeEditTab === '2' && !hasProofread);
  const saveTooltip = editLocked
    ? editLockMessage
    : (activeEditTab === '2' && !hasProofread)
      ? 'Cannot save: proofread content does not exist'
      : 'Save changes';
  const editDisabled = editLocked || activeEditTab === '3' || (activeEditTab === '1' && hasProofread) || (activeEditTab === '2' && !hasProofread);
  const editTooltip = editLocked
    ? editLockMessage
    : activeEditTab === '3'
      ? 'Editing notes cannot be edited directly.'
    : activeEditTab === '1' && hasProofread
      ? 'Cannot edit original content while a proofread version exists. Switch to the Proofread tab or delete it.'
      : activeEditTab === '2' && !hasProofread
        ? 'Cannot edit proofread content before it exists.'
        : 'Edit Content';
  const atBackup = useMemo(() => {
    if (!backupContent) return false;
    return normalizeForCompare(backupContent) === normalizeForCompare(currentContentForCompare);
  }, [backupContent, currentContentForCompare]);
  const currentVariantKey = useMemo(
    () => getCurrentVariantKey(),
    [selectedChapter.chapter_guid, selectedTab, activeEditTab]
  );

  const storageKey = useMemo(
    () => `reverted:${currentVariantKey}`,
    [currentVariantKey]
  );

  // Load reverted state from localStorage when storageKey changes
  useEffect(() => {
    try {
      const v = localStorage.getItem(storageKey);
      setLocalReverted(v === '1');
    } catch {
      setLocalReverted(false);
    }
  }, [storageKey]);

  // Only clear reverted state when content is actually modified, not during navigation
  useEffect(() => {
    // Don't run this effect during initial load or navigation - only when content is being modified
    if (!contentModified) return;
    
    if (!backupContent) {
      // If no backup exists, clear any stale localStorage entries
      try { localStorage.removeItem(storageKey); } catch {}
      setLocalReverted(false);
      return;
    }
    
    const equal = normalizeForCompare(backupContent) === normalizeForCompare(currentContentForCompare);
    if (!equal) {
      // Content has diverged from backup due to user modification
      try { localStorage.removeItem(storageKey); } catch {}
      setLocalReverted(false);
    }
    
    // Reset the modification flag after processing
    setContentModified(false);
  }, [contentModified, storageKey, backupContent, currentContentForCompare]);

  // Warn user if navigating away with unsaved edits
  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (isEditingContent && hasUnsavedChanges) {
        e.preventDefault();
        // Chrome requires returnValue to be set
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [isEditingContent, hasUnsavedChanges]);

  // Separate effect to handle the case where content matches backup (for initial loads)
  useEffect(() => {
    // Only run when we have both backup and current content, and we're not in a modification cycle
    if (!backupContent || !currentContentForCompare || contentModified) return;
    
    const equal = normalizeForCompare(backupContent) === normalizeForCompare(currentContentForCompare);
    if (equal) {
      // Content matches backup, check if we should mark as reverted
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored === '1') {
          setLocalReverted(true);
        }
      } catch {}
    }
  }, [backupContent, currentContentForCompare, storageKey, contentModified]);

  const isRevertPending = revertPendingKey === currentVariantKey;
  const revertDisabled = editLocked || !hasBackup || atBackup || isRevertPending || localReverted;
  const revertTooltip = editLocked
    ? editLockMessage
    : !hasBackup
    ? 'Cannot revert, no changes have been made to the chapter'
    : isRevertPending
      ? 'Reverting to original uploaded version'
      : (atBackup || localReverted)
        ? 'Already at original uploaded version'
        : 'Revert to original uploaded version';
  const chapterLanguageOptions = useMemo<ActiveLanguageOption[]>(() => [
    { value: 'Original', label: originalLanguageLabel },
    ...((book.translation_pairs || []).map((pair) => ({
      value: pair,
      label: formatLangDisplayText(pair),
    }))),
  ], [book.translation_pairs, formatLangDisplayText, originalLanguageLabel]);
  const currentVariantLabel = getCurrentVariantLabel();
  const currentVersionHasPendingProofreadReview = useMemo(() => {
    if (selectedTab === 'Original') {
      return hasPendingProofreadReviewForRow(selectedChapter);
    }
    const { sourceLang, targetLang } = splitLanguagePair(selectedTab);
    const translatedChapter = (book as any)[`translated_chapters_${sourceLang}_${targetLang}`]
      ?.find((chapter: any) => chapter.chapter_guid === selectedChapter.chapter_guid);
    return hasPendingProofreadReviewForRow(translatedChapter);
  }, [book, selectedChapter, selectedTab]);

  const getVariantStatus = ({
    hasContent,
    hasWarning,
    isProcessing,
  }: {
    hasContent: boolean;
    hasWarning?: boolean;
    isProcessing?: boolean;
  }): ChapterVariantStatus => {
    if (hasWarning) {
      return 'attention';
    }
    if (isProcessing && !hasContent) {
      return 'processing';
    }
    return hasContent ? 'ready' : 'missing';
  };

  const renderVersionTabLabel = (
    label: string,
    status: ChapterVariantStatus,
    icon?: React.ReactNode,
  ) => {
    const statusLabel =
      status === 'ready'
        ? 'Ready'
        : status === 'attention'
          ? 'Attention'
          : status === 'processing'
            ? 'Processing'
            : 'Missing';
    return (
      <span className="chapter-version-tab">
        <span className="chapter-version-tab__label">
          {icon}
          <span>{label}</span>
        </span>
        <span className={`chapter-version-tab__status chapter-version-tab__status--${status}`}>
          {statusLabel}
        </span>
      </span>
    );
  };

  const selectedChapterHasWarning = rejectedChapters.includes(selectedChapter.chapter_name);
  const originalSourceStatus = getVariantStatus({
    hasContent: Boolean(String(selectedChapter.content || '').trim()),
    hasWarning: selectedChapterHasWarning,
  });
  const handleProofreadCurrentChapter = () => {
    toggleProofreadModal(
      selectedTab && selectedTab !== 'Original' ? selectedTab : undefined,
    );
  };

  const handleAskAIForCurrentChapter = () => {
    if (!onAskAI) return;
    const payload = {
      chapterGuid: selectedChapter.chapter_guid,
      variant: getCurrentContentType(),
      variantLabel: getCurrentVariantLabel(),
      chapterHtml: getContentForActiveTab() || '',
      originalChapterHtml: getOriginalChapterHtmlForAskAI(),
    };
    onAskAI(payload);
  };

  const moreActionsMenu = (
    <Menu data-testid="tutorial:chapter:more-menu">
      {isAdmin && (
        <Menu.Item key="askAI" onClick={handleAskAIForCurrentChapter} data-testid="tutorial:chapter:more-ask-ai">
          Ask AI about this chapter
        </Menu.Item>
      )}
      {!isEditingContent && (
        <>
          <Menu.Item key="translateBook" onClick={handleTranslateBookClick} data-testid="tutorial:chapter:more-translate-book">
            Translate Book
          </Menu.Item>
          <Menu.Item key="proofreadBook" onClick={handleProofreadBookClick} data-testid="tutorial:chapter:more-proofread-book">
            Proofread whole book
          </Menu.Item>
          <Menu.Item key="editMetadata" onClick={handleEditToggle} disabled={isEditing} data-testid="tutorial:chapter:more-edit-metadata">
            Edit Metadata
          </Menu.Item>
        </>
      )}
      <Menu.Item key="revertChapter" onClick={handleRevertContent} disabled={revertDisabled} data-testid="tutorial:chapter:more-revert-chapter">
        Revert to uploaded version
      </Menu.Item>
    </Menu>
  );

  return (
    <div 
      ref={containerRef}
      data-testid="tutorial:book-detail:chapter-workspace"
      className="chapter-workspace"
    >
      <div
        data-tour-id="chapter-header"
        data-testid="tutorial:book-detail:chapter-header"
        className="chapter-workspace-toolbar book-detail-secondary-surface"
      >
        <div
          className="chapter-language-control"
        >
          <ActiveLanguageSelector
            value={selectedTab}
            onChange={handleTabChange}
            options={chapterLanguageOptions}
            disabled={isEditingContent}
            sourceLanguage={originalLanguageLabel}
            originalValue="Original"
            contextLabel="Viewing edition"
            ariaLabel="Current chapter edition"
            dataTourId="chapter-language-select"
            dataTestId="tutorial:book-detail:language-selector"
          />
          {rejectedChapters.includes(selectedChapter.chapter_name) && (
            <Tooltip
              title={
                <>
                  <div>
                    The AI may reject this translation for NSFW, violence, or sometimes unknown reasons.
                  </div>
                  <div>Next steps: Retry the translation. Retry with another model.</div>
                </>
              }
            >
              <span className="chapter-warning-pill">
                <ExclamationCircleOutlined /> Content Warning
              </span>
            </Tooltip>
          )}
        </div>

        <div
          data-tour-id="chapter-actions"
          data-testid="tutorial:book-detail:actions-toolbar"
          className="chapter-actions"
        >
          {isEditingContent ? (
            <div className="chapter-edit-action-group" data-testid="tutorial:chapter:edit-actions">
              <Tooltip title={saveTooltip}>
                <Button
                  data-tour-id="editor-save"
                  data-testid="tutorial:chapter:save-action"
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveContent}
                  disabled={saveDisabled}
                  className="chapter-workflow-action chapter-workflow-action--primary"
                >
                  Save chapter
                </Button>
              </Tooltip>
              <Tooltip title="Cancel editing">
                <Button icon={<CloseOutlined />} onClick={handleCancelEdit} data-testid="tutorial:chapter:cancel-edit">
                  Cancel
                </Button>
              </Tooltip>
            </div>
          ) : (
            <>
              <Tooltip title="Translate this chapter">
                <Button
                  data-tour-id="chapter-primary-action"
                  data-testid="tutorial:translation:open-chapter"
                  type="primary"
                  icon={<TranslationOutlined />}
                  onClick={toggleTranslateModal}
                  className="chapter-workflow-action chapter-workflow-action--primary"
                >
                  Translate
                </Button>
              </Tooltip>
              <Tooltip title="Proofread this chapter">
                <Button
                  data-testid="tutorial:proofread:open-chapter"
                  icon={<CheckOutlined />}
                  onClick={handleProofreadCurrentChapter}
                  className="chapter-workflow-action"
                >
                  Proofread chapter
                </Button>
              </Tooltip>
              <Tooltip title={editTooltip}>
                <Button
                  data-testid="tutorial:chapter:edit-action"
                  icon={<EditOutlined />}
                  onClick={handleEditContent}
                  disabled={editDisabled}
                  className="chapter-workflow-action"
                >
                  Edit
                </Button>
              </Tooltip>
              <Tooltip title="Export this book">
                <Button
                  data-testid="tutorial:export:open-chapter"
                  icon={<ExportOutlined />}
                  onClick={handleExportBookClick}
                  className="chapter-workflow-action"
                >
                  Export
                </Button>
              </Tooltip>
            </>
          )}
          <Tooltip title="More actions">
            <Dropdown overlay={moreActionsMenu} trigger={['click']}>
              <Button icon={<MoreOutlined />} data-testid="tutorial:chapter:more-actions">More</Button>
            </Dropdown>
          </Tooltip>
        </div>
      </div>

      <div
        className="chapter-tab-workspace chapter-reading-workspace"
      >
        <div className="chapter-tab-actions-slot" ref={setTabActionsContainer} />
        {isEditingContent && (
          <div className="chapter-editing-status-row" aria-live="polite" data-testid="tutorial:chapter:editing-state">
            <span className="chapter-editing-status-row__label">
              Editing {currentVariantLabel}
            </span>
            <span
              className={`chapter-editing-status-row__state ${
                hasUnsavedChanges ? 'chapter-editing-status-row__state--dirty' : ''
              }`}
            >
              {hasUnsavedChanges ? 'Unsaved changes' : 'No changes'}
            </span>
          </div>
        )}
        <Tabs
          data-tour-id="chapter-tabs"
          data-testid="tutorial:book-detail:tabs"
          defaultActiveKey="1"
          activeKey={activeEditTab}
          className="chapter-version-tabs"
          onChange={(key) => {
            if (!isEditingContent) {
              setActiveEditTab(key as '1' | '2' | '3');
            } else {
              message.warning(
                'Please save or cancel editing before switching tabs'
              );
            }
          }}
          tabBarStyle={{ fontWeight: '500' }}
        >
          {selectedTab === 'Original' ? (
            <>
              <TabPane
                tab={
                  <span data-testid="tutorial:chapter:text-tab">
                    {renderVersionTabLabel(
                      originalLanguageLabel,
                      originalSourceStatus,
                      <FileTextOutlined />,
                    )}
                  </span>
                }
                key="1"
              >
                <div className="chapter-tab-pane">
                  {renderEditableContent(selectedChapter.content)}
                </div>
              </TabPane>
              {(() => {
                const { content, isProofread, editingNotesContent, hasEditingNotes } = renderProofreadTabContent(
                  'Original',
                  'Original',
                  tabActionsContainer,
                  activeEditTab === '2',
                );
                return (
                  <>
                    <TabPane
                      tab={
                      <span data-testid="tutorial:chapter:proofread-tab">
                        {renderVersionTabLabel(
                          currentVersionHasPendingProofreadReview ? 'Review proofread changes' : 'Proofread',
                          getVariantStatus({
                            hasContent: isProofread,
                            isProcessing: proofreadProcessing,
                          }),
                        )}
                      </span>
                      }
                      key="2"
                    >
                      <div className="chapter-tab-pane">
                        {isEditingContent
                          ? renderEditableContent(getContentForActiveTab())
                          : content}
                      </div>
                    </TabPane>
                    <TabPane
                      tab={
                        <span data-testid="tutorial:chapter:editing-notes-tab">
                          {renderVersionTabLabel(
                            'Editing Notes',
                            getVariantStatus({ hasContent: Boolean(hasEditingNotes) }),
                          )}
                        </span>
                      }
                      key="3"
                    >
                      <div className="chapter-tab-pane">
                        {editingNotesContent}
                      </div>
                    </TabPane>
                  </>
                );
              })()}
            </>
          ) : (
            <>
              {(() => {
                const { sourceLang, targetLang } = splitLanguagePair(selectedTab);
                const { content, isTranslated } = renderTranslatedTabContent(
                  sourceLang,
                  targetLang,
                  tabActionsContainer,
                  activeEditTab === '1',
                );
                return (
                  <TabPane
                    tab={
                      <span data-testid="tutorial:chapter:text-tab">
                        {renderVersionTabLabel(
                          formatLangDisplayText(selectedTab),
                          getVariantStatus({
                            hasContent: isTranslated,
                            hasWarning: selectedChapterHasWarning,
                          }),
                        )}
                      </span>
                    }
                    key="1"
                  >
                    <div className="chapter-tab-pane">
                      {isEditingContent
                        ? renderEditableContent(getContentForActiveTab())
                        : content}
                    </div>
                  </TabPane>
                );
              })()}
              {(() => {
                const { sourceLang, targetLang } = splitLanguagePair(selectedTab);
                const { content, isProofread } = renderProofreadTabContent(
                  sourceLang,
                  targetLang,
                  tabActionsContainer,
                  activeEditTab === '2',
                );
                return (
                  <TabPane
                    tab={
                      <span data-testid="tutorial:chapter:proofread-tab">
                        {renderVersionTabLabel(
                          currentVersionHasPendingProofreadReview ? 'Review proofread changes' : 'Proofread',
                          getVariantStatus({
                            hasContent: isProofread,
                            isProcessing: proofreadProcessing,
                          }),
                        )}
                      </span>
                    }
                    key="2"
                  >
                    <div className="chapter-tab-pane">
                      {isEditingContent
                        ? renderEditableContent(getContentForActiveTab())
                        : content}
                    </div>
                  </TabPane>
                );
              })()}
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default ChapterContentPanel;
